import { getApiBaseUrl } from '../../../utils/apiConfig';
import { loadScheduleData } from '../services/scheduleService';
import {
  getLocalDateString,
  getTodayLocal,
  formatDateToPTBR,
  calculateTimeDifference,
  formatNumber,
  translatePreventivaType,
} from './formatters';
import { getContractEquipment, getCounters, getLastOS, getTB02112Field, searchContracts, searchEquipment } from '../services/equipmentService';
import { searchDefeitos } from '../services/defeitoService';

/** Cache de prefixo — mesma ideia do EquipmentStep / useEquipmentSearch */
const equipCache = { prefix: '', type: '', allResults: [] };

/** Normaliza série/patrimônio para comparação (ignora espaços). */
export function normEquipSearchToken(value) {
  return String(value || '').toUpperCase().replace(/\s+/g, '');
}

/** Termo enviado à API: estoque usa busca completa; contrato mantém prefixo de 4 chars. */
export function getEquipSearchApiTerm(clean, type) {
  if (type === 'estoque') return clean;
  return clean.substring(0, 4);
}

/** Chave de cache alinhada ao termo da API por tipo. */
export function getEquipSearchCacheKey(clean, type) {
  return getEquipSearchApiTerm(clean, type);
}

export function filterEquipResults(allResults, cleanSearch) {
  const q = normEquipSearchToken(cleanSearch);
  if (!q) return [];
  return (allResults || []).filter((item) => {
    const serie = normEquipSearchToken(item.serie);
    const patrimonio = normEquipSearchToken(item.patrimonio);
    return serie.includes(q) || patrimonio.includes(q);
  });
}

export function clearOpenOsV2EquipSearchCache() {
  equipCache.prefix = '';
  equipCache.type = '';
  equipCache.allResults = [];
}

function extractEquipmentModelFilter(raw) {
  if (!raw) return null;
  const b = String(
    raw.idBrand ?? raw.modelo_id_marca ?? raw.marca ?? raw.TB01010_MARCA ?? ''
  ).trim();
  const s = String(
    raw.idSubgroup ?? raw.modelo_id_subgrupo ?? raw.subgrupo ?? raw.TB01010_SUBGRUPO ?? ''
  ).trim();
  if (b && s) return { idBrand: b, idSubgroup: s };
  return null;
}

function getLoggedUser() {
  try {
    return JSON.parse(localStorage.getItem('userData') || 'null');
  } catch {
    return null;
  }
}

function isCoordinatorUser(user) {
  if (!user) return false;
  if (user.coordinator === true || user.coordinator === 1 || user.coordinator === '1') return true;
  const type = String(user.type != null ? user.type : '').trim().toLowerCase();
  if (type.includes('coorden')) return true;
  return type === 'usuário' || type === 'usuario' || type === '0' || user.type === 0 || user.type === '0';
}

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Segunda a quinta = dia seguinte; sexta/sábado/domingo = próxima segunda. */
function getScheduleTomorrowDate(from = new Date()) {
  const currentDay = from.getDay();
  const year = from.getFullYear();
  const month = from.getMonth();
  const day = from.getDate();
  if (currentDay >= 1 && currentDay <= 4) {
    return new Date(year, month, day + 1);
  }
  const daysUntilMonday = currentDay === 0 ? 1 : 8 - currentDay;
  return new Date(year, month, day + daysUntilMonday);
}

function addOneCalendarDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

export function getOpenOsV2FutureDateBounds() {
  const tomorrow = getScheduleTomorrowDate();
  const max = new Date();
  max.setFullYear(max.getFullYear() + 2);
  return {
    minDate: getLocalDateString(addOneCalendarDay(tomorrow)),
    maxDate: getLocalDateString(max),
  };
}

async function filterAreasForLoggedCoordinator(areas) {
  const user = getLoggedUser();
  if (!isCoordinatorUser(user) || user.id == null) return areas;
  try {
    const r = await fetch(`${getApiBaseUrl()}/api/team/areacoord`, { headers: getAuthHeaders() });
    const j = await r.json().catch(() => ({}));
    if (!j.success || !Array.isArray(j.data)) return [];
    const allowed = new Set(
      j.data
        .filter((link) => String(link.id_coordinator) === String(user.id))
        .map((link) => String(link.id_area))
    );
    return areas.filter((area) => allowed.has(String(area.id)));
  } catch {
    return [];
  }
}


function isContractExpired(contrVenc) {
  if (!contrVenc) return false;
  const vencDate = new Date(contrVenc);
  const today = new Date();
  vencDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return !Number.isNaN(vencDate.getTime()) && vencDate < today;
}

function safeDate(value) {
  if (!value) return '';
  try {
    return formatDateToPTBR(value) || '';
  } catch {
    return '';
  }
}

/** Mapeia suggestion de equipamento → campos do mock (confirmação). */
export function mapEquipConfirm(raw) {
  const r = raw || {};
  const instalado = safeDate(r.dtinstalacao) || '—';
  const clienteLabel = [r.codigo, r.cliente].filter(Boolean).join(' ').trim() || '—';
  return {
    equip: {
      serie: r.serie || '',
      patrimonio: r.patrimonio || '',
      modelo: r.produto || r.modelo || '—',
      instalado,
      raw: r,
    },
    clienteLabel,
    endereco: String(r.Endereco || '').trim() || '—',
    localInterno: String(r.Endereco_Local || '').trim(),
    contratoVencido: isContractExpired(r.contr_venc),
  };
}

/** Mapeia getLastOS → shape `ultimo` do mock. */
/** Dias corridos entre a data (só dia; DATETIME do MS SQL chega como UTC) e hoje; null se inválida. */
function daysSinceDateOnly(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - day) / 86400000);
}

export function mapLastOsToMock(lastOsDate, lastOsData) {
  if (!lastOsData) return null;
  const d = lastOsData;
  const dateStr = safeDate(lastOsDate || d.dataFim) || '—';
  const horaIni = d.horaIni ? String(d.horaIni).substring(0, 5) : '';
  const horaFim = d.horaFim ? String(d.horaFim).substring(0, 5) : '';
  const tempo =
    horaIni && horaFim ? calculateTimeDifference(d.horaIni, d.horaFim) : '';
  const laudoDataParts = [dateStr];
  if (horaIni && horaFim) laudoDataParts.push(`${horaIni} → ${horaFim}`);
  if (tempo && tempo !== 'N/A') laudoDataParts.push(`tempo ${tempo}`);

  const contadores = [];
  const pushCont = (label, valor) => {
    if (valor == null || valor === '') return;
    contadores.push({ label, valor: formatNumber(valor) });
  };
  pushCont('P&B', d.contador);
  pushCont('Color', d.contadorC);
  pushCont('DG', d.contadorDG);
  pushCont('A3 P&B', d.contadorGF);
  pushCont('A3 Color', d.contadorGFC);

  return {
    os: String(d.codigo || '—'),
    data: dateStr,
    tipo: translatePreventivaType(d.preventiva),
    tecnico: d.tecnico || '—',
    solicitante: d.solicitante || '—',
    motivo: d.nome || '—',
    defeito: d.defeito || '—',
    defeitoAlert: String(d.defeitoAlert || d.defeito_alert || '').trim(),
    colorBg: String(d.colorBg || d.color_bg || '').trim(),
    observacao: d.obsDefeito || d.obs || '—',
    solucao: d.solucaoDefeito || '—',
    laudoData: laudoDataParts.join(' · '),
    laudoTexto: d.obsTecnico || d.obs || '—',
    condicao: d.condicao || '—',
    contadores,
  };
}

function mapContractItemToEquipRaw(item) {
  const end = (item && item.endereco) || {};
  const num = end.numero != null && String(end.numero).trim() !== '' ? String(end.numero).trim() : '0';
  return {
    ...(item || {}),
    serie: item.numSerie || '',
    patrimonio: item.patrimonio || '',
    produto: item.modelo || '',
    modelo: item.modelo || '',
    codigo: item.codigo || '',
    codCli: item.codCli || '',
    codprod: item.codProduto || '',
    codProduto: item.codProduto || '',
    codEmp: item.codEmp || '',
    codSite: item.codSite || '',
    Endereco: item.enderecoCompleto || '',
    Endereco_Local: end.local || '',
    end_logradouro: end.logradouro || '',
    end_num: num,
    end_comp: end.complemento || '',
    end_bairro: end.bairro || '',
    end_cidade: end.cidade || '',
    end_estado: end.estado || '',
    end_cep: end.cep || '',
    emp_nome: item.emp_nome || '',
    emp_cnpj: item.emp_cnpj || '',
    emp_endereco: item.emp_endereco || '',
    idBrand: item.marca != null ? String(item.marca).trim() : '',
    idSubgroup: item.subgrupo != null ? String(item.subgrupo).trim() : '',
    contr_venc: item.contr_venc || item.contrVenc || null,
  };
}

export function formatSiteAddressFromRaw(raw) {
  if (!raw) return '';
  const end = raw.endereco || {};
  const parts = [
    end.logradouro || raw.end_logradouro,
    end.numero || raw.end_num,
    end.bairro || raw.end_bairro,
    end.cidade || raw.end_cidade,
  ]
    .map((p) => String(p || '').trim())
    .filter((p) => p && p !== '0');
  if (parts.length) return parts.join(', ');
  return String(raw.enderecoCompleto || raw.Endereco || '').replace(/^[, ]+|[, ]+$/g, '').trim();
}

/** Chave normalizada para comparar se dois equipamentos compartilham o mesmo endereço. */
export function addressKeyFromEquipRaw(raw) {
  if (!raw) return '';
  const end = raw.endereco || {};
  const parts = [
    end.logradouro || raw.end_logradouro,
    end.numero || raw.end_num,
    end.bairro || raw.end_bairro,
    end.cidade || raw.end_cidade,
    end.estado || raw.end_estado,
    end.cep || raw.end_cep,
  ]
    .map((p) => String(p || '').trim().toUpperCase())
    .filter((p) => p && p !== '0');
  if (parts.length) return parts.join('|');
  return String(raw.Endereco || raw.enderecoCompleto || formatSiteAddressFromRaw(raw) || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

/**
 * Campos END_* / LOCALIZACAO para create OS.
 * `parsed` = correção manual (ViaCEP etc.); sem parsed usa flat end_* ou endereco aninhado do contrato.
 */
export function locationFieldsFromEquipRaw(raw, { localInterno = '', parsed = null } = {}) {
  const r = raw || {};
  const end = r.endereco || {};
  const hasCorrected = !!(parsed && String(parsed.logradouro || '').trim());
  const pick = (corr, flat, nested, fallback) => {
    if (hasCorrected) {
      const t = String(corr == null ? '' : corr).trim();
      return t || fallback || '';
    }
    const a = String(flat == null ? '' : flat).trim();
    if (a) return a;
    const b = String(nested == null ? '' : nested).trim();
    if (b) return b;
    return fallback || '';
  };
  const localFromRaw = end.local || r.Endereco_Local || r.Endereco_Local || '';
  return {
    LOCALIZACAO: String(localInterno || localFromRaw || '').trim(),
    END_LOGRADOURO: pick(parsed && parsed.logradouro, r.end_logradouro, end.logradouro),
    END_NUM: pick(parsed && parsed.numero, r.end_num, end.numero, '0') || '0',
    END_LOGRADOURO_COMP: pick(parsed && parsed.complemento, r.end_comp, end.complemento),
    END_BAIRRO: pick(parsed && parsed.bairro, r.end_bairro, end.bairro),
    END_CIDADE: pick(parsed && parsed.cidade, r.end_cidade, end.cidade),
    END_ESTADO: pick(parsed && parsed.estado, r.end_estado, end.estado),
    END_CEP: pick(parsed && parsed.cep, r.end_cep, end.cep),
  };
}

export function formatLocationLabel(loc) {
  if (!loc) return '';
  return [loc.END_LOGRADOURO, loc.END_NUM, loc.END_BAIRRO, loc.END_CIDADE, loc.END_ESTADO]
    .map((p) => String(p || '').trim())
    .filter((p) => p && p !== '0')
    .join(', ');
}

/** Site > modelo > séries — mesma agrupação da EquipmentSelectionStep v1. */
export function mapContractEquipmentToLocais(equipment) {
  const groups = {};
  (equipment || []).forEach((item) => {
    const siteKey = item.siteNome || 'Sem site';
    const modeloKey = item.modelo || 'Sem modelo';
    if (!groups[siteKey]) groups[siteKey] = {};
    if (!groups[siteKey][modeloKey]) groups[siteKey][modeloKey] = [];
    groups[siteKey][modeloKey].push(item);
  });
  return Object.keys(groups)
    .map((siteKey) => {
      const modelos = Object.keys(groups[siteKey])
        .map((modeloKey) => ({
          nome: modeloKey,
          series: groups[siteKey][modeloKey].map((item) => ({
            serie: item.numSerie || item.patrimonio || '',
            patrimonio: item.patrimonio || '',
            raw: mapContractItemToEquipRaw(item),
          })),
        }))
        .sort((a, b) => b.series.length - a.series.length);
      const firstRaw = modelos[0] && modelos[0].series[0] ? modelos[0].series[0].raw : null;
      return {
        nome: siteKey,
        modelos,
        endereco: formatSiteAddressFromRaw(firstRaw),
        total: modelos.reduce((n, m) => n + m.series.length, 0),
      };
    })
    .sort((a, b) => b.total - a.total)
    .map(({ nome, modelos, endereco }) => ({ nome, modelos, endereco }));
}

export function contractCodigoFromHit(h) {
  const raw = (h && h.raw) || {};
  const direct = String(raw.codigo || (h && h.codigo) || '').trim();
  if (direct) return direct;
  const classif = String(raw.classif || (h && (h.classif || h.titulo)) || '');
  const m = classif.match(/^\(\s*([^)]+?)\s*\)/);
  return m ? String(m[1]).trim() : '';
}

function mapEquipHit(e) {
  const instalado = safeDate(e.dtinstalacao);
  return {
    kind: 'equip',
    serie: e.serie,
    patrimonio: e.patrimonio || '',
    modelo: e.produto || e.modelo || '',
    instalado,
    titulo: `${e.serie}${e.patrimonio ? ` • ${e.patrimonio}` : ''}`,
    sub: e.produto || e.modelo || '',
    tag: e.codigo ? 'Contrato' : 'Estoque',
    raw: e,
  };
}

/**
 * Ponte mock Abrir OS v2 ↔ APIs reais (busca + confirmação + última OS).
 */
export function installOpenOsV2SearchBridge() {
  window.__openOsV2Search = async ({ query, mode, origem }) => {
    const clean = String(query || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
    if (clean.length < 4) return [];

    if (mode === 'multipla' && origem !== 'estoque') {
      const result = await searchContracts(clean);
      if (!result.success) return [];
      return (result.suggestions || []).map((c) => ({
        kind: 'cliente',
        codigo: c.codigo,
        nome: c.classif || c.nome || '',
        classif: c.classif || c.nome || '',
        titulo: c.classif || c.codigo,
        sub: c.codigo && c.classif && c.classif !== c.codigo ? c.codigo : c.nome || '',
        tag: 'Cliente',
        raw: c,
      }));
    }

    const type = origem === 'estoque' ? 'estoque' : 'contrato';
    const cacheKey = getEquipSearchCacheKey(clean, type);
    const apiTerm = getEquipSearchApiTerm(clean, type);

    if (equipCache.prefix === cacheKey && equipCache.type === type) {
      return filterEquipResults(equipCache.allResults, clean).map(mapEquipHit);
    }

    const result = await searchEquipment(apiTerm, type, true);
    if (!result.success) return [];

    equipCache.prefix = cacheKey;
    equipCache.type = type;
    equipCache.allResults = result.suggestions || [];

    return filterEquipResults(equipCache.allResults, clean).map(mapEquipHit);
  };

  window.__openOsV2FetchContractEquipment = async (contrato) => {
    const codigo = String(contrato || '').trim();
    if (!codigo) return [];
    const result = await getContractEquipment(codigo);
    if (!result.success) return [];
    return mapContractEquipmentToLocais(result.equipment || []);
  };

  window.__openOsV2MapEquip = (raw) => mapEquipConfirm(raw);

  window.__openOsV2FetchLastOs = async (serie) => {
    if (!serie) return null;
    const result = await getLastOS(serie);
    if (!result.success || !result.lastOsData) return null;
    const mapped = mapLastOsToMock(result.lastOsDate, result.lastOsData);
    if (mapped) mapped.diasDesdeAbertura = daysSinceDateOnly(result.lastOsDate);
    return mapped;
  };

  window.__openOsV2FetchTB02112Field = async (serie, contrato, field) => {
    if (!serie || !contrato || !field) return { value: null };
    const result = await getTB02112Field(serie, contrato, field);
    return { value: result?.value ?? null };
  };

  window.__openOsV2SearchDefeitos = async ({ search, codEquip }) => {
    const term = String(search || '').trim();
    if (!term) return [];
    const ce = codEquip != null && String(codEquip).trim() !== '' ? String(codEquip).trim() : null;
    const result = await searchDefeitos(term, ce);
    return result.success ? (result.items || []) : [];
  };

  window.__openOsV2FetchCounters = async (serie) => {
    if (!serie) return { ContadorPB: 0, ContadorCor: 0, ContadorTotal: 0 };
    const result = await getCounters(serie);
    return {
      ContadorPB: result?.ContadorPB ?? 0,
      ContadorCor: result?.ContadorCor ?? 0,
      ContadorTotal: result?.ContadorTotal ?? 0,
    };
  };

  window.__openOsV2FetchDefeitoEquipAssocFlag = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const base = getApiBaseUrl();
      const r = await fetch(`${base}/api/config/defeitos-verve/equip-os-assoc-flag`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const j = await r.json().catch(() => ({}));
      return !!j.enabled;
    } catch {
      return false;
    }
  };

  window.__openOsV2LoadScheduleData = async (equipRaw) => {
    const result = await loadScheduleData(extractEquipmentModelFilter(equipRaw));
    const areas = result.success ? result.data || [] : [];
    return filterAreasForLoggedCoordinator(areas);
  };

  window.__openOsV2SearchAddress = async (query) => {
    const q = String(query || '').replace(/\s+/g, ' ').trim();
    if (q.length < 4) return [];
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/maps/address-search?q=${encodeURIComponent(q)}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json().catch(() => null);
      if (!data || !data.success) return [];
      return Array.isArray(data.items) ? data.items : [];
    } catch {
      return [];
    }
  };

  window.__openOsV2ResolveAddress = async (item, query) => {
    if (item && item.parsed && (item.display || item.texto)) return item;
    const placeId = item && item.placeId;
    if (!placeId) return item || null;
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/api/maps/address-details?placeId=${encodeURIComponent(placeId)}&q=${encodeURIComponent(query || '')}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json().catch(() => null);
      if (!data || !data.success) return item || null;
      return data.item || item;
    } catch {
      return item || null;
    }
  };

  window.__openOsV2ScheduleCalendar = () => {
    const today = new Date();
    const tomorrow = getScheduleTomorrowDate(today);
    const minFuture = addOneCalendarDay(tomorrow);
    const dow = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
    return {
      todayIso: getTodayLocal(),
      tomorrowIso: getLocalDateString(tomorrow),
      minFutureIso: getLocalDateString(minFuture),
      hojeDia: String(today.getDate()).padStart(2, '0'),
      hojeDow: dow[today.getDay()],
      amanhaDia: String(tomorrow.getDate()).padStart(2, '0'),
      amanhaDow: dow[tomorrow.getDay()],
    };
  };
}

export function uninstallOpenOsV2SearchBridge() {
  try {
    delete window.__openOsV2Search;
    delete window.__openOsV2FetchContractEquipment;
    delete window.__openOsV2MapEquip;
    delete window.__openOsV2FetchLastOs;
    delete window.__openOsV2FetchTB02112Field;
    delete window.__openOsV2SearchDefeitos;
    delete window.__openOsV2FetchCounters;
    delete window.__openOsV2FetchDefeitoEquipAssocFlag;
    delete window.__openOsV2LoadScheduleData;
    delete window.__openOsV2SearchAddress;
    delete window.__openOsV2ResolveAddress;
    delete window.__openOsV2ScheduleCalendar;
  } catch {
    window.__openOsV2Search = undefined;
    window.__openOsV2FetchContractEquipment = undefined;
    window.__openOsV2MapEquip = undefined;
    window.__openOsV2FetchLastOs = undefined;
    window.__openOsV2FetchTB02112Field = undefined;
    window.__openOsV2SearchDefeitos = undefined;
    window.__openOsV2FetchCounters = undefined;
    window.__openOsV2FetchDefeitoEquipAssocFlag = undefined;
    window.__openOsV2LoadScheduleData = undefined;
    window.__openOsV2SearchAddress = undefined;
    window.__openOsV2ResolveAddress = undefined;
    window.__openOsV2ScheduleCalendar = undefined;
  }
  clearOpenOsV2EquipSearchCache();
}
